import { createContext, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { apiV1 } from './../lib/axios';
import { uploadS3 } from '../services/uploadS3';
import { fileToBase64 } from '../utils/fileToBase64';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '../utils/getApiErrorMessage';
import { getDocumentFormat } from '../utils/documentFormat';
import { documentTypes } from '../services/enums';

export const RegisterContext = createContext({});

export function RegisterProvider({ children }) {
  const[document, setDocument] = useState()
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [companyDataPj, setCompanyDataPj] = useState({});
  const [addressPj, setAddressPj] = useState({});
  const [representativePj, setRepresentativePj] = useState([]);
  const [documentsPj, setDocumentsPj] = useState({});
  const [personType, setPersonType] = useState('');

  const postCompanyDataPj = (data) => {
    const companyTypes = {
      0 : "SA",
      1 : "LTDA",
      2 : "MEI",
      3 : "ME",
      6 : "SA_FECHADA"
    }

    setPersonType(companyTypes[data.companyType]);
    setCompanyDataPj(data);
    setStep(2);
  };

  const postMfaData = () => {
    setStep(3);
  };

  const postAddressFormPj = (data) => {
    const payload = {
      addressLine: data.street,
      addressLine2: data.number,
      zipCode: data.cep.replace(/\D/g, ''),
      neighborhood: data.neighborhood,
      cityName: data.city,
      state: data.state,
      addressType: 0,
      country: 'BRA',
      complement: data.complement,
      cityCode: data.ddd,
    };
    setAddressPj(payload);
    setStep(4);
  };

  const postRepresentativeFormPj = async (data) => {
    const representatives = await Promise.all(
      data.map(async (representative)=>{
        const representativeDocuments = [];
  
        if (representative.documentType === 'CNH') {
          const cnh = await fileToBase64( representative.documentFront.item(0) )
    
          representativeDocuments.push({
            base64: cnh,
            mimetype: representative.documentFront.item(0).type.split('/')[1],
          })
        } else {
          const rgFront = await fileToBase64( representative.documentFront.item(0) )
    
          representativeDocuments.push({
            base64: rgFront,
            mimetype: representative.documentFront.item(0).type.split('/')[1],
          });
    
          const rgBack = await fileToBase64( representative.documentBack.item(0))
    
          representativeDocuments.push({
            base64: rgBack,
            mimetype: representative.documentBack.item(0).type.split('/')[1],
          });
        }

        return {
          name: representative.name,
          taxNumber: representative.cpf.replace(/\D/g, ''),
          mail: representative.email,
          phoneNumber: representative.phone.replace(/[\s()-]/g, ''),
          birthDate: representative.birthDate,
          motherFullName: representative.motherName,
          gender: representative.gender,
          maritalStatus: representative.maritalStatus,
          birthCity: representative.birthCity,
          birthState: representative.birthState,
          documentType: representative.documentType,
          document: representativeDocuments
        };
      })
    )

    setRepresentativePj(representatives);
    setStep(5);
  };

  const postDocumentsFormPj = async (data) => {
    const documents = {};

    if (data.socialContract?.length) {
      const socialContract = await fileToBase64(
        data.socialContract.item(0)
      ).then((response) => response);

      documents['socialContract'] = {
        base64: socialContract,
        mimetype: data.socialContract.item(0).type.split('/')[1],
      };
    }

    if (data.proofIncome?.length) {
      const proofIncome = await fileToBase64(data.proofIncome.item(0)).then(
        (response) => response
      );

      documents['proofIncome'] = {
        base64: proofIncome,
        mimetype: data.proofIncome.item(0).type.split('/')[1],
      };
    }

    const proofRegistration = await fileToBase64(
      data.proofRegistration.item(0)
    ).then((response) => response);

    documents['proofRegistration'] = {
      base64: proofRegistration,
      mimetype: data.proofRegistration.item(0).type.split('/')[1],
    };

    setDocumentsPj(documents);

    setStep(6);

    // await registerPj(documents);
  };

  const pjRegisterPerson = async () => {
    const data = {
      type: 1,
      companyType: companyDataPj.companyType,
      companyActivity: companyDataPj.companyActivity,
      name: companyDataPj.fantasyName,
      legalName: companyDataPj.socialReason,
      constitutionDate: companyDataPj.constitutionDate,
      phoneNumber: companyDataPj.phone.replace(/[\s()-]/g, ''),
      taxNumber: companyDataPj.cnpj.replace(/\D/g, ''),
      mail: companyDataPj.email,
      motherFullName: representativePj[0].motherName,
      nationality: 'BRA',
      birthState: addressPj.stateCode,
      birthDate: representativePj[0].birthDate,
      servicePackageId: '1',
      birthCity: addressPj.city,
      birthState: addressPj.state,
      invoicing: companyDataPj.monthlyIncome ?? 0,
      password: companyDataPj.password
    };

    const personId = await apiV1
      .post('/person/store', data)
      .then((response) => {
        return response.data.person.id;
      })
      .catch((err) => {

      });

    return personId;
  };

  const pjRegisterRepresentative = async (personId, representativePj) => {
    const data = {
      personId,
      name: representativePj.name,
      taxNumber: representativePj.cpf.replace(/\D/g, ''),
      mail: representativePj.email,
      phoneNumber: representativePj.phone.replace(/[\s()-]/g, ''),
      birthDate: representativePj.birthDate,
      motherFullName: representativePj.motherName,
      gender: representativePj.gender,
      maritalStatus: representativePj.maritalStatus,
      birthCity: representativePj.birthCity,
      birthState: representativePj.birthState,
    };

    const representativeId = await apiV1
      .post('/representative/store', data)
      .then((response) => {
        return response.data.data.id;
      })
      .catch((err) => {

      });

    if (representativePj.documentType === 'CNH') {
      const cnh = await fileToBase64(
        representativePj.documentFront.item(0)
      ).then((response) => response);

      const cnhFile = await uploadS3(
        `cnh.${representativePj.documentFront.item(0).type.split('/')[1]}`,
        cnh
      );

      await pjRegisterDocuments({
        personId,
        representativeId,
        type: documentTypes.CNHFrente,
        name: 'CNH',
        format: getDocumentFormat(
          representativePj.documentFront.item(0).type.split('/')[1]
        ),
        file: cnhFile,
      });
    } else {
      const rgFront = await fileToBase64(
        representativePj.documentFront.item(0)
      ).then((response) => response);

      const rgFrontFile = await uploadS3(
        `rgFront.${representativePj.documentFront.item(0).type.split('/')[1]}`,
        rgFront
      );

      await pjRegisterDocuments({
        personId,
        representativeId,
        type: documentTypes.rgFrente,
        name: 'RG Frente',
        format: getDocumentFormat(
          representativePj.documentFront.item(0).type.split('/')[1]
        ),
        file: rgFrontFile,
      });

      const rgBack = await fileToBase64(
        representativePj.documentBack.item(0)
      ).then((response) => response);

      const rgBackFile = await uploadS3(
        `rgBack.${representativePj.documentBack.item(0).type.split('/')[1]}`,
        rgBack
      );

      await pjRegisterDocuments({
        personId,
        representativeId,
        type: documentTypes.rgVerso,
        name: 'RG Verso',
        format: getDocumentFormat(
          representativePj.documentBack.item(0).type.split('/')[1]
        ),
        file: rgBackFile,
      });
    }

    return representativeId;
  };

  const pjRegisterDocuments = async ({
    personId,
    representativeId,
    type,
    name,
    format,
    file,
  }) => {
    const data = {
      personId,
      representativeId,
      documentFile: file,
      documentFormat: format,
      documentName: name,
      documentType: type,
      description: name,
    };

    await apiV1
      .post('/document/store', data)
      .then(() => {})
      .catch((err) => {

      });
  };

  const registerPj = async () => {
    try {
      setLoading(true);

      const documents = await saveCompanyDocumentsAtBucket(documentsPj);
      const representatives = await mountRepresentativePayload(representativePj);

      const payload = {
        name: companyDataPj.fantasyName,
        legalName: companyDataPj.socialReason,
        constitutionDate: companyDataPj.constitutionDate,
        phoneNumber: companyDataPj.phone.replace(/\D/g, ''),
        taxNumber: companyDataPj.cnpj.replace(/\D/g, ''),
        mail: companyDataPj.email,
        companyActivity: companyDataPj.companyActivity,
        companyType: companyDataPj.companyType,
        invoicing: companyDataPj.monthlyIncome,
        password: companyDataPj.password,
        address: addressPj,
        representatives,
        documents,
      }

      await apiV1.post('/person/company/register', payload)

      setSuccess(true);
      setLoading(false);
    } catch (err) {
      console.log("err ", err);
      setSuccess(false);
      setLoading(false);
      toast.error(getApiErrorMessage(err, 'Erro ao realizar cadastro'));
    }
  };

  const formatInvoicing = (invoicing) => {
    const unmaskedInvoicing = invoicing.replace('.', '').replace(',','.');
    return parseFloat(unmaskedInvoicing);
  }

  const saveCompanyDocumentsAtBucket = async (documents) => {
    const companyDocuments = [];

    if (documents.socialContract) {
      const socialContract = await uploadS3(
        `contrato-social.${documents.socialContract.mimetype}`,
        documents.socialContract.base64
      );

      companyDocuments.push({
        type: documentTypes.socialContract,
        format: getDocumentFormat( documents.socialContract.mimetype ),
        file: socialContract,
      })
    }

    if (documents.proofIncome) {
      const proofIncome = await uploadS3(
        `comprovante-renda.${documents.proofIncome.mimetype}`,
        documents.proofIncome.base64
      );

      companyDocuments.push({
        type: documentTypes.compRenda,
        format: getDocumentFormat( documents.proofIncome.mimetype ),
        file: proofIncome,
      })
    }
    const proofRegistration = await uploadS3(
      `cartao-cnpj.${documents.proofRegistration.mimetype}`,
      documents.proofRegistration.base64
    );

    companyDocuments.push({
      type: documentTypes.CNPJ,
      format: getDocumentFormat( documents.proofRegistration.mimetype ),
      file: proofRegistration,
    })

    return companyDocuments;
  }

  const mountRepresentativePayload = async (representatives) => {
    return Promise.all(
      representatives.map(async (representative)=>{
        const document = [];
  
        if (representative.documentType === 'CNH') {
          const cnhFile = await uploadS3(
            `cnh.${representative.document[0].mimetype}`,
            representative.document[0].base64
          );
    
          document.push({
            type: documentTypes.CNHFrente,
            format: getDocumentFormat( representative.document[0].mimetype ),
            file: cnhFile,
          })
        } else {
          const rgFrontFile = await uploadS3(
            `rgFront.${representative.document[0].mimetype}`,
            representative.document[0].base64
          );
  
          document.push({
            type: documentTypes.rgFrente,
            format: getDocumentFormat( representative.document[0].mimetype ),
            file: rgFrontFile,
          })
          
          const rgBackFile = await uploadS3(
            `rgBack.${representative.document[1].mimetype}`,
            representative.document[1].base64
          );
  
          document.push({
            type: documentTypes.rgVerso,
            format: getDocumentFormat( representative.document[1].mimetype ),
            file: rgBackFile,
          })
        }
  
        return {
          ...representative,
          document
        }
      })
    ) 
  }

  const postDocumentsPixExtract = async (data) => {
    const documents = {};
  
    if (data.extrato?.length) {
      try {
        const extrato = await fileToBase64(data.extrato.item(0))
          .then((response) => response);
  
        documents['extrato'] = {
          base64: extrato,
          mimetype: data.extrato.item(0).type.split('/')[1],
        };
      } catch (error) {
        console.error('Erro ao converter extrato para base64:', error);
        toast.error('Erro ao processar o extrato');
        return;
      }
    }
  
    setDocumentsPj(documents);
    setStep(5);
  
    try {
      let proofIncomeLink = '';
      if (documents.extrato) {
        proofIncomeLink = await uploadS3(
          `comprovante-renda.${documents.extrato.mimetype}`,
          documents.extrato.base64
        );
  
        console.log('Link do Comprovante de Renda:', proofIncomeLink);
  
        await pjRegisterDocuments({
          personId,
          representativeId: representativeIds[0],
          type: documentTypes.compRenda,
          name: 'Comprovante Renda',
          format: getDocumentFormat(documents.extrato.mimetype),
          file: proofIncomeLink,
        });
      }
  
      let proofRegistrationLink = '';
      if (data.proofRegistration?.length) {
        const proofRegistrationFile = await fileToBase64(data.proofRegistration.item(0))
          .then((response) => response);
  
        proofRegistrationLink = await uploadS3(
          `cartao-cnpj.${data.proofRegistration.item(0).type.split('/')[1]}`,
          proofRegistrationFile
        );
  
        console.log('Link do Comprovante de Registro:', proofRegistrationLink);
  
        await pjRegisterDocuments({
          personId,
          representativeId: representativeIds[0],
          type: documentTypes.compRegistro,
          name: 'Comprovante Registro',
          format: getDocumentFormat(data.proofRegistration.item(0).type.split('/')[1]),
          file: proofRegistrationLink,
        });
      }
    } catch (error) {
      console.error('Erro ao fazer upload ou registrar documentos:', error);
      toast.error(getApiErrorMessage(error, 'Erro ao registrar documentos'));
    }
  
    return documents;
  }; 

  const registerExtract = async () => {
    try {
      setLoading(true);
  
      const documents = documentsPj;
  
      // Verifica se o documento de extrato está presente
      if (!documents.extrato) {
        toast.error('Documento de extrato não encontrado.');
        setLoading(false);
        return;
      }
  
      const personId = await pjRegisterPerson();
      if (!personId) {
        toast.error('Não foi possível concluir o cadastro.');
        setLoading(false);
        return;
      }
  
      let representativeIds = [];
  
      for (const item of representativePj) {
        const repId = await pjRegisterRepresentative(personId, item);
        representativeIds.push(repId);
      }
  
      // Upload do extrato para o S3
      const extratoBase64 = await fileToBase64(documents.extrato.file);
      const extrato = await uploadS3(
        `extrato.${documents.extrato.mimetype}`,
        extratoBase64
      );
  
      // Registro do extrato nos documentos PJ
      await RegisterDocuments({
        personId,
        representativeId: representativeIds[0],
        type: documentTypes.extrato, // Certifique-se de que 'extrato' está definido corretamente em documentTypes
        name: 'Extrato',
        format: getDocumentFormat(documents.extrato.mimetype),
        file: extrato,
      });
  
      setSuccess(true);
      setLoading(false);
    } catch (err) {
      console.error('Erro ao registrar o extrato:', err);
      toast.error(getApiErrorMessage(err, 'Erro ao registrar o extrato.'));
      setLoading(false);
    }
  };
  return (
    <RegisterContext.Provider
      value={{
        success,
        step,
        loading,
        postCompanyDataPj,
        postAddressFormPj,
        postRepresentativeFormPj,
        postDocumentsFormPj,
        registerPj,
        setStep,
        companyDataPj,
        representativePj,
        personType,
        addressPj,
        postDocumentsPixExtract,
        registerExtract,
        document,
        setDocument,
        postMfaData,
      }}

    >
      <Outlet />
    </RegisterContext.Provider>
  );
}
